'use client'

import { useOpenCookieBanner } from '@artus_engineering/react-gdpr-cookie-consent'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { DateTime } from 'luxon'
import Image from 'next/image'
import Link from 'next/link'
import Container from '../Container'
import { getSocialProfiles } from './socialData'

type MenuItem = MenuLink | MenuAction

interface MenuLink {
    name: string
    href: string
    target?: string
    Icon?: any
}

interface MenuAction {
    name: string
    onClick: () => void
}

interface Menu {
    features: MenuItem[]
    support: MenuItem[]
    links: MenuItem[]
    legal: MenuItem[]
    social: MenuItem[]
}

function Item({ item }: { item: MenuItem }) {
    if ('onClick' in item) {
        return (
            <li key={item.name} className="list-none">
                <button
                    type="button"
                    onClick={item.onClick}
                    className="text-sm text-primary-100 hover:text-primary-200"
                >
                    {item.name}
                </button>
            </li>
        )
    }

    if (!item.Icon) {
        return (
            <li key={item.name} className="list-none">
                <Link
                    href={
                        item.href.startsWith('https://')
                            ? item.href
                            : process.env.NEXT_PUBLIC_LANDING_BASE_URL + item.href
                    }
                    target={item.target}
                    className="text-sm text-primary-100 hover:text-primary-200"
                >
                    {item.name}
                </Link>
            </li>
        )
    }
    return (
        <Link
            key={item.name}
            href={item.href.startsWith('https://') ? item.href : process.env.NEXT_PUBLIC_LANDING_BASE_URL + item.href}
            target="_blank"
        >
            <item.Icon
                className="h-8 w-8 mb-2 text-primary-200 hover:scale-105 hover:text-primary-100 hover:drop-shadow-sm transition-all duration-300"
                aria-hidden="true"
                alt={item.name}
            />
            <span className="sr-only">{item.name}</span>
        </Link>
    )
}

export function Footer() {
    const { t } = useComponentTranslation()
    const handleOpenCookieBanner = useOpenCookieBanner()
    const socialProfiles = getSocialProfiles(t)

    const navigation: Menu = {
        features: [
            {
                name: t('layout.landing.menu.features.appointmentManagement.title'),
                href: t('layout.landing.menu.features.appointmentManagement.href')
            },
            {
                name: t('layout.landing.menu.features.cancellationFee.title'),
                href: t('layout.landing.menu.features.cancellationFee.href')
            },
            {
                name: t('layout.landing.menu.features.onlineMeeting.title'),
                href: t('layout.landing.menu.features.onlineMeeting.href')
            },
            {
                name: t('layout.landing.menu.features.calendarIntegration.title'),
                href: t('layout.landing.menu.features.calendarIntegration.href')
            },
            {
                name: t('layout.landing.menu.features.clientBooking.title'),
                href: t('layout.landing.menu.features.clientBooking.href')
            },
            {
                name: t('layout.landing.menu.features.individualNotifications.title'),
                href: t('layout.landing.menu.features.individualNotifications.href')
            },
            {
                name: t('layout.landing.menu.features.dataProtection.title'),
                href: t('layout.landing.menu.features.dataProtection.href')
            }
        ],
        support: [
            { name: t('layout.landing.menu.contact'), href: '/kontakt' },
            { name: t('layout.landing.menu.documentation'), href: '/dokumentation' }
        ],
        links: [
            { name: t('layout.landing.menu.aboutUs'), href: '/ueber-uns' },
            { name: t('layout.landing.menu.roadmap'), href: '/roadmap' },
            { name: t('layout.landing.menu.register'), href: '/registrieren' }
        ],
        legal: [
            { name: t('layout.landing.menu.impress'), href: '/impressum' },
            { name: t('layout.landing.menu.privacyDeclaration'), href: '/datenschutzerklaerung' },
            { name: t('layout.landing.menu.terms'), href: '/agb' },
            { name: t('layout.landing.menu.avv'), href: '/avv' },
            { name: t('layout.landing.menu.cookieSettings'), onClick: handleOpenCookieBanner }
        ],
        social: socialProfiles
    }

    return (
        <div className="bg-linear-to-br from-primary-600 to-primary-700 min-h-full min-w-full bg-no-repeat bg-cover pt-12 relative">
            <footer>
                <Container className="pb-14">
                    <h2 id="footer-heading" className="sr-only">
                        {t('layout.landing.footerTitle')}
                    </h2>
                    <div className="xl:grid 2xl:grid-cols-3 xl:gap-8 mt-12">
                        <div className="space-y-8 xl:col-span-1 max-w-sm">
                            <Image
                                className="h-14 w-auto"
                                style={{ width: 'auto' }}
                                width={256}
                                height={256}
                                src="/assets/logoDarkBg.svg"
                                alt="Tebuto Logo"
                            />
                            <p className="text-primary-200">{t('layout.landing.slogan')}</p>
                            <div className="flex space-x-6">
                                {navigation.social.map((item) => (
                                    <Item key={item.name} item={item} />
                                ))}
                            </div>
                        </div>
                        <div className="mt-12 grid md:grid-cols-2 gap-8 xl:mt-0 xl:col-span-2">
                            <div className="md:grid lg:grid-cols-2 md:gap-8">
                                <div>
                                    <h3 className="text-sm font-semibold text-white  tracking-wider uppercase">
                                        {t('layout.landing.menu.features.title')}
                                    </h3>
                                    <ul className="mt-4 space-y-2">
                                        {navigation.features.map((item) => (
                                            <Item key={item.name} item={item} />
                                        ))}
                                    </ul>
                                </div>
                                <div className="mt-12 md:mt-0">
                                    <h3 className="text-sm font-semibold text-white tracking-wider uppercase">
                                        {t('layout.landing.menu.support')}
                                    </h3>
                                    <ul className="mt-4 space-y-2">
                                        {navigation.support.map((item) => (
                                            <Item key={item.name} item={item} />
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <div className="md:grid lg:grid-cols-2 md:gap-8">
                                <div>
                                    <h3 className="text-sm font-semibold text-white  tracking-wider uppercase">
                                        {t('layout.landing.menu.links')}
                                    </h3>
                                    <ul className="mt-4 space-y-2">
                                        {navigation.links.map((item) => (
                                            <Item key={item.name} item={item} />
                                        ))}
                                    </ul>
                                </div>
                                <div className="mt-12 md:mt-0">
                                    <h3 className="text-sm font-semibold text-white tracking-wider uppercase">
                                        {t('layout.landing.menu.law')}
                                    </h3>
                                    <ul className="mt-4 space-y-2">
                                        {navigation.legal.map((item) => (
                                            <Item key={item.name} item={item} />
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <Link
                                href="https://heydata.eu"
                                target="_blank"
                                title="heyData GDPR seal"
                                rel="noreferrer noopener"
                                className="w-max"
                            >
                                <Image
                                    alt="heyData trusted logo"
                                    src="https://privacy-seal.heydata.eu/seal/80c7a878-b096-4897-9435-d5d07e0c9d3f?lang=de&design=design2&sealType=GDPR"
                                    className="border-none h-auto size-32"
                                    width="192"
                                    height="192"
                                />
                            </Link>
                        </div>
                    </div>
                    <div className="mt-12 border-t border-primary-800/20 pt-8">
                        <p className="text-sm text-primary-300 text-left tracking-wide font-light">
                            &copy; {t('copyright', { year: DateTime.now().year })}
                        </p>
                    </div>
                </Container>
            </footer>
        </div>
    )
}
